import { useQuery as useReactQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { QueryResult, QueryOptions } from './QueryTypes';

const useQuery = <TData = unknown, TError = RequestExceptionWithResponse>(
    options: QueryOptions<TData, TError>,
): QueryResult<TData, TError> => {
    const queryClient = useQueryClient();
    const queryState = useReactQuery<TData, TError>(options);
    const queryIsLoading = useMemo(
        () => queryState.isLoading && queryState.fetchStatus !== 'idle',
        [queryState.isLoading, queryState.fetchStatus],
    );

    const successCallback = useRef(options.onSuccess);
    const errorCallback = useRef(options.onError);

    useEffect(() => {
        if (queryState.isSuccess && !queryIsLoading && successCallback.current) {
            successCallback.current(queryState.data);
        }
    }, [queryIsLoading, queryState.isSuccess, queryState.data]);

    useEffect(() => {
        if (queryState.isError && !queryIsLoading && errorCallback.current) {
            errorCallback.current(queryState.error);
        }
    }, [queryIsLoading, queryState.isError, queryState.error]);

    return {
        ...queryState,
        invalidateQuery: () => queryClient.invalidateQueries({ queryKey: options.queryKey }),
    };
};

export default useQuery;
